import {
  Clock,
  Server,
  Package,
  GitBranch,
  Activity,
  X,
  RefreshCw,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { createLogger } from "../../lib/logger";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";

const log = createLogger("DeploymentDrawer");

interface DeploymentDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deploymentId: string;
  serviceId?: string;
}

interface DeploymentDetails {
  id: string;
  deploymentId: string;
  status: string;
  serviceName: string;
  serviceType: string;
  environment: string;
  branch: string;
  port: number;
  createdAt: string;
}

interface DeploymentLog {
  timestamp: string;
  stage: string;
  message: string;
  level: string;
}

export function DeploymentDrawer({
  open,
  onOpenChange,
  deploymentId,
  serviceId: _serviceId,
}: DeploymentDrawerProps) {
  const [deployment, setDeployment] = useState<DeploymentDetails | null>(null);
  const [logs, setLogs] = useState<DeploymentLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchDeploymentDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await api.deployments.get(deploymentId);
      const deploymentData = response.deployment;

      // Map backend fields to component state
      setDeployment({
        id: deploymentData.id,
        deploymentId: deploymentId,
        status: deploymentData.status,
        serviceName: deploymentData.name,
        serviceType: deploymentData.framework || "Unknown",
        environment: deploymentData.environment,
        branch: deploymentData.branch || "main",
        port: deploymentData.port,
        createdAt: deploymentData.createdAt,
      });
    } catch (error) {
      log.error("Failed to fetch deployment details", error);
    } finally {
      setLoading(false);
    }
  }, [deploymentId]);

  const fetchDeploymentLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const response: any = await api.deployments.getLogs(deploymentId);
      if (response && response.pipelineLogs) {
        setLogs(response.pipelineLogs);
      }
    } catch (error) {
      log.error("Failed to fetch deployment logs", error);
    } finally {
      setLogsLoading(false);
    }
  }, [deploymentId]);

  useEffect(() => {
    if (open && deploymentId) {
      fetchDeploymentDetails();
      fetchDeploymentLogs();
    }
  }, [open, deploymentId, fetchDeploymentDetails, fetchDeploymentLogs]);

  // Separate effect for polling
  useEffect(() => {
    if (!open || !deploymentId) return;

    // Poll for logs every 3 seconds while deployment is in progress
    const interval = setInterval(() => {
      if (
        deployment?.status === "building" ||
        deployment?.status === "deploying" ||
        deployment?.status === "pending"
      ) {
        fetchDeploymentLogs();
        fetchDeploymentDetails();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [
    open,
    deploymentId,
    deployment?.status,
    fetchDeploymentDetails,
    fetchDeploymentLogs,
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
      case "completed":
        return "bg-success";
      case "building":
      case "deploying":
      case "pending":
        return "bg-primary";
      case "failed":
      case "error":
        return "bg-destructive";
      default:
        return "bg-muted-foreground";
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case "error":
        return "text-destructive";
      case "warning":
        return "text-warning";
      case "success":
        return "text-success";
      case "info":
      default:
        return "text-primary";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[600px] sm:max-w-full">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl">Deployment Details</SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <SheetDescription>
            Monitor your deployment progress and view logs
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : deployment ? (
          <div className="mt-6 space-y-6">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <Badge
                className={`${getStatusColor(
                  deployment.status
                )} text-white px-3 py-1`}
              >
                {deployment.status.toUpperCase()}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  fetchDeploymentDetails();
                  fetchDeploymentLogs();
                }}
                disabled={logsLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${
                    logsLoading ? "animate-spin" : ""
                  }`}
                />
                Refresh
              </Button>
            </div>

            {/* Service Information */}
            <div className="space-y-4">
              <h3 className="text-h5 text-muted-foreground">
                SERVICE DETAILS
              </h3>
              <div className="grid gap-3">
                <div className="flex items-center gap-3">
                  <Server className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {deployment.serviceName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Service Name
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {deployment.serviceType}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Service Type
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <GitBranch className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {deployment.branch || "main"}
                    </p>
                    <p className="text-xs text-muted-foreground">Branch</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {deployment.environment}
                    </p>
                    <p className="text-xs text-muted-foreground">Environment</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {new Date(deployment.createdAt).toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">Started At</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Deployment Logs */}
            <div className="space-y-4">
              <h3 className="text-h5 text-muted-foreground">
                DEPLOYMENT LOGS
              </h3>
              <ScrollArea className="h-[400px] w-full rounded-md bg-muted/50 p-4">
                {logs.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                    {logsLoading ? (
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Loading logs...
                      </div>
                    ) : (
                      "No logs available yet"
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 font-mono text-xs">
                    {logs.map((log, index) => (
                      <div key={index} className="flex gap-2">
                        <span className="text-muted-foreground">
                          {formatTimestamp(log.timestamp)}
                        </span>
                        {log.stage && (
                          <span className="text-purple-400">[{log.stage}]</span>
                        )}
                        <span className={getLogLevelColor(log.level)}>
                          {log.message}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Deployment not found
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
